import { useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { History, Landmark, Loader2, Pencil, Plus } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { BORDER, FormDrawer } from "@/components/shared/FormDrawer";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { formatCurrency } from "@/modules/expenses/components/CurrencyAmount";
import {
  ACCOUNT_TYPE_LABELS,
  useAccountOptions,
  useFinanceAccounts,
  useSaveAccount,
  type FinanceAccount,
  type FinanceAccountType,
} from "../hooks/useFinanceAccounts";

const ICON_BUTTON =
  "inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-[hsl(210,50%,96%)] hover:text-foreground";

function AccountDrawer({
  account,
  onClose,
}: {
  account: FinanceAccount | null;
  onClose: () => void;
}) {
  const save = useSaveAccount();
  const { data: options } = useAccountOptions();
  const isEdit = !!account;

  const [name, setName] = useState(account?.name ?? "");
  const [type, setType] = useState<FinanceAccountType>(account?.type ?? "BANK");
  const [companyId, setCompanyId] = useState(account?.companyId ?? "");
  const [partnerId, setPartnerId] = useState(account?.partnerId ?? "");
  const [bankName, setBankName] = useState(account?.bankName ?? "");
  const [accountLast4, setAccountLast4] = useState(account?.accountLast4 ?? "");
  const [notes, setNotes] = useState(account?.notes ?? "");
  const [isActive, setIsActive] = useState(account?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const company = options?.companies.find((c) => c.id === companyId);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const bank = type === "BANK" ? { bankName: bankName || null, accountLast4: accountLast4 || null } : {};
    try {
      await save.mutateAsync(
        isEdit
          ? { id: account.id, payload: { name, notes: notes || null, isActive, ...bank } }
          : {
              payload: {
                name,
                type,
                companyId,
                partnerId: type === "PARTNER_HOLDING" ? partnerId : null,
                notes: notes || null,
                ...bank,
              },
            },
      );
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <FormDrawer
      title={isEdit ? `Edit Account: ${account.code}` : "New Account"}
      onClose={onClose}
      onSubmit={handleSubmit}
      error={error}
      busy={save.isPending}
    >
      {isEdit ? (
        <p className="text-xs text-muted-foreground">
          {account.company.name} · {ACCOUNT_TYPE_LABELS[account.type]} · {account.currency}
          {account.partner ? ` · held by ${account.partner.name}` : ""}. Company, type, currency and
          partner cannot be changed.
        </p>
      ) : (
        <>
          <div className="space-y-2">
            <Label htmlFor="acc-company">
              Company <span className="text-destructive">*</span>
            </Label>
            <Select id="acc-company" value={companyId} onChange={(e) => setCompanyId(e.target.value)} required>
              <option value="">Select a company...</option>
              {options?.companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.currency})
                </option>
              ))}
            </Select>
            {company && (
              <p className="text-xs text-muted-foreground">
                The account will hold {company.currency}, the company's currency.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="acc-type">
              Type <span className="text-destructive">*</span>
            </Label>
            <Select
              id="acc-type"
              value={type}
              onChange={(e) => setType(e.target.value as FinanceAccountType)}
            >
              {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          {type === "PARTNER_HOLDING" && (
            <div className="space-y-2">
              <Label htmlFor="acc-partner">
                Held by <span className="text-destructive">*</span>
              </Label>
              <Select
                id="acc-partner"
                value={partnerId}
                onChange={(e) => setPartnerId(e.target.value)}
                required
              >
                <option value="">Select a partner...</option>
                {options?.partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.shortCode})
                  </option>
                ))}
              </Select>
              <p className="text-xs text-muted-foreground">
                Company money held by this partner rather than in the company bank.
              </p>
            </div>
          )}
        </>
      )}

      <div className="space-y-2">
        <Label htmlFor="acc-name">
          Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="acc-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. PX bank"
          required
        />
      </div>

      {type === "BANK" && (
        <div className="grid grid-cols-[1fr_120px] gap-3">
          <div className="space-y-2">
            <Label htmlFor="acc-bank">Bank name</Label>
            <Input id="acc-bank" value={bankName} onChange={(e) => setBankName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="acc-last4">Last 4 digits</Label>
            <Input
              id="acc-last4"
              value={accountLast4}
              onChange={(e) => setAccountLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
              inputMode="numeric"
              pattern="\d{4}"
              placeholder="1234"
            />
          </div>
          <p className="col-span-2 text-xs text-muted-foreground">
            Only the last 4 digits are stored. Never enter the full account number or IBAN.
          </p>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="acc-notes">Notes</Label>
        <Input id="acc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      {isEdit && (
        <div className="space-y-2">
          <Label htmlFor="acc-status">Status</Label>
          <Select
            id="acc-status"
            value={isActive ? "active" : "inactive"}
            onChange={(e) => setIsActive(e.target.value === "active")}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
          <p className="text-xs text-muted-foreground">
            Accounts are never deleted, so their history is kept. Make an account inactive to stop
            using it.
          </p>
        </div>
      )}
    </FormDrawer>
  );
}

export default function AccountsPage() {
  const { user } = useAuth();
  const permissions = user?.permissions?.finance_accounts;
  const [showInactive, setShowInactive] = useState(false);
  const { data: accounts = [], isLoading, isError, error } = useFinanceAccounts(showInactive);
  const [drawer, setDrawer] = useState<{ account: FinanceAccount | null } | null>(null);
  const [activity, setActivity] = useState<FinanceAccount | null>(null);

  const byCompany = useMemo(() => {
    const groups = new Map<
      string,
      { name: string; currency: string; total: number; accounts: FinanceAccount[] }
    >();
    for (const a of accounts) {
      const group = groups.get(a.companyId) ?? {
        name: a.company.name,
        currency: a.currency,
        total: 0,
        accounts: [],
      };
      group.accounts.push(a);
      // Sum in cents so the company total has no floating-point drift.
      group.total = Math.round(group.total * 100 + a.balance * 100) / 100;
      groups.set(a.companyId, group);
    }
    return [...groups.entries()];
  }, [accounts]);

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Finance" },
        { label: "Accounts" },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Landmark className="h-[18px] w-[18px] text-foreground" strokeWidth={1.5} />
            <h1 className="text-2xl font-semibold tracking-tight">Accounts</h1>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show inactive
            </label>
            {permissions?.canExport && <ExportButton module="finance-accounts" />}
            {permissions?.canCreate && (
              <Button onClick={() => setDrawer({ account: null })}>
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                New Account
              </Button>
            )}
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Balances are calculated from each account's entries. Open an account to see its statement,
          enter an opening balance or make an adjustment.
        </p>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load accounts</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : isLoading ? (
          <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" strokeWidth={1.5} />
        ) : byCompany.length === 0 ? (
          <div className="rounded-xl border bg-white p-8 text-center text-sm text-muted-foreground" style={BORDER}>
            No accounts yet
          </div>
        ) : (
          byCompany.map(([companyId, group]) => (
            <div key={companyId} className="overflow-hidden rounded-xl border bg-white" style={BORDER}>
              <div className="border-b px-6 py-3 font-semibold" style={BORDER}>
                {group.name}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground" style={BORDER}>
                    <th className="px-6 py-3 font-medium">#</th>
                    <th className="px-6 py-3 font-medium">Name</th>
                    <th className="px-6 py-3 font-medium">Type</th>
                    <th className="px-6 py-3 font-medium">Currency</th>
                    <th className="px-6 py-3 font-medium">Held by / Bank</th>
                    <th className="px-6 py-3 font-medium">Status</th>
                    <th className="px-6 py-3 text-right font-medium">Balance</th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {group.accounts.map((a) => (
                    <tr key={a.id} className="border-b last:border-0" style={BORDER}>
                      <td className="px-6 py-4 font-mono text-xs text-muted-foreground">{a.code}</td>
                      <td className="px-6 py-4 font-medium">
                        <Link to={`/finance/accounts/${a.id}`} className="hover:underline">
                          {a.name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">{ACCOUNT_TYPE_LABELS[a.type]}</td>
                      <td className="px-6 py-4 text-muted-foreground">{a.currency}</td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {a.partner?.name ??
                          (a.bankName
                            ? `${a.bankName}${a.accountLast4 ? ` ····${a.accountLast4}` : ""}`
                            : "—")}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground">
                        {a.isActive ? "Active" : "Inactive"}
                      </td>
                      <td className="px-6 py-4 text-right tabular-nums">
                        {formatCurrency(a.balance, a.currency)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => setActivity(a)}
                          className={ICON_BUTTON}
                          title="Activity history"
                        >
                          <History className="h-[14px] w-[14px]" strokeWidth={1.5} />
                        </button>
                        {permissions?.canEdit && (
                          <button
                            type="button"
                            onClick={() => setDrawer({ account: a })}
                            className={ICON_BUTTON}
                            title="Edit"
                          >
                            <Pencil className="h-[14px] w-[14px]" strokeWidth={1.5} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-semibold" style={BORDER}>
                    <td className="px-6 py-3" colSpan={6}>
                      Total {group.name}
                    </td>
                    <td className="px-6 py-3 text-right tabular-nums">
                      {formatCurrency(group.total, group.currency)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          ))
        )}
      </div>

      {drawer && <AccountDrawer account={drawer.account} onClose={() => setDrawer(null)} />}
      {activity && (
        <ActivityHistoryDrawer
          isOpen
          onClose={() => setActivity(null)}
          entityType="finance_account"
          entityId={activity.id}
          entityLabel={activity.name}
        />
      )}
    </AppLayout>
  );
}
